// ═══════════════════════════════════════════════════════════════════════════
// useDashboardData — hook para consultar Firestore desde un dashboard
// ───────────────────────────────────────────────────────────────────────────
// ⚠️ VIVE EN `src/core/hooks/` A PROPÓSITO (16-ago-2026, ítem 18). Nació en
// `src/modules/mercadolibre/hooks/` y se mudó acá porque la regla que declara
// hasta dónde lee una consulta (`src/core/lib/cotaDeLectura.js`) es
// **horizontal**, y su enforcement —el checker de CI— tiene que poder exigirla
// en todo el árbol. Con el hook adentro de un vertical, la plataforma habría
// dependido de un módulo para su propio candado, que es exactamente lo que la
// regla de oro del repo prohíbe: los verticales dependen de la plataforma,
// nunca al revés. Efecto colateral que conviene saber: acá SÍ entra en el
// scope de cobertura (`vitest.config.js`), que sólo incluye `src/core/`.
// No tiene nada de Mercado Libre — sus únicos imports son React, Firestore y
// dos helpers de `@core/lib`. Si algún día los tuviera, el que los agregue
// está rompiendo esto.
//
// Usa onSnapshot + persistentLocalCache → datos instantáneos desde IndexedDB,
// luego actualiza silenciosamente cuando llega del servidor.
//
// - data es siempre un array, y trae TODO lo que matchea la query (salvo que
//   se pase `limit`). Nunca se recorta por `cap`.
// - loading es true desde el montaje hasta el primer snapshot: en una
//   colección grande, es lo que la pantalla tiene que mostrar como "cargando".
// - fromCache indica si los datos actuales vienen del cache local.
// - Cada doc tiene `id` (id del documento) + todos los campos del doc.
//
// `cap` es un UMBRAL DE ALARMA, no un recorte (16-ago-2026, ítem 18): la
// lectura trae TODO y, si supera el umbral, devuelve `capExcedido=true` y lo
// reporta como error. La pantalla queda completa; lo que se avisa es que el
// volumen creció. `limit` sigue siendo la cota silenciosa, para listas cortas
// donde traer de más no tiene sentido. Ver `superaUmbral` en `@core/lib/cotaDeLectura`.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useRef } from 'react'
import { collection, onSnapshot, query, where, orderBy, limit as fbLimit } from 'firebase/firestore'
import { db } from '@core/lib/firebase'
import { createLogger } from '@core/lib/logger'
import { docConId } from '@core/lib/docConId'
import { wheresDeFiltros } from '@core/lib/filtrosDeQuery'
import {
  resolverCota, SIN_DECLARAR_EN_PROD, superaUmbral,
  MENSAJE_UMBRAL_EXCEDIDO, MENSAJE_SIN_COTA,
} from '@core/lib/cotaDeLectura'

const logger = createLogger('useDashboardData')

// Firestore rechaza todo `limit()` > 10000 en UNA query ("Limit value in the
// structured query is over the maximum value of 10000"). Sólo aplica a `limit`,
// que es la cota SILENCIOSA; `cap` ya no genera ningún `limit()` (ver abajo).
export const FIRESTORE_MAX_LIMIT = 10000

/*
 * ⭐⭐ `cap` ES UN UMBRAL, NO UN RECORTE (16-ago-2026, decisión de Mariano)
 * ────────────────────────────────────────────────────────────────────────
 * Hasta hoy `cap: N` pedía `limit(N+1)` y hacía `docs.slice(0, N)`. Dos
 * problemas, y el segundo estaba VIVO en producción:
 *
 * 1. **El recorte no elegía.** De los 14 call sites con `cap`, **13 no
 *    declaran `orderBy`** (el único que sí es `CentralPromocionesDashboard`,
 *    que ordena por `fechaCreada`). Sin `orderBy`, Firestore ordena por nombre
 *    de documento y el `slice` se queda con documentos al azar. Textual:
 *    *"no tenemos forma de saber, quizás estamos dejando fuera publicaciones
 *    con mucho tráfico y ventas"*.
 *
 *    ⚠️ El caso que sí ordenaba se migró a `limit`, que es lo que significa
 *    "quiero las N más recientes, a propósito". `cap` y `limit` dejan de ser
 *    intercambiables: `limit` recorta con criterio, `cap` sólo mide.
 * 2. **Y en cuatro pantallas lo hacía en silencio**: `CuadroGeneral` ×2 y
 *    `TraficoConversion` ×2 tienen cap y ni siquiera desestructuran
 *    `capExcedido`. Justo las que calculan ACOS y costo de publicidad.
 *
 * Ahora `cap` no toca la query: se lee todo, se cuenta lo que volvió y si
 * supera el umbral se prende `capExcedido` y se reporta con `logger.error`.
 * La pantalla queda completa —el número que muestra es el verdadero— y el
 * umbral pasa a ser lo que siempre tuvo que ser: una ALARMA de volumen.
 *
 * Lo que esto disuelve de paso: el camino paginado con cursor y su escalada
 * existían **sólo** para servir el `limit(cap+1)` que ya no se pide. Sin
 * `limit`, ninguna lectura cruza el tope duro de Firestore, así que ninguna
 * pierde el listener vivo — ni siquiera el tenant de ~20k publicaciones que
 * originó ADR 016, que es exactamente al que la maquinaria vieja castigaba.
 *
 * ⚠️ Y el costo, dicho sin maquillar y en las dos direcciones:
 *
 *   · para los caps > 10.000 el cambio ABARATA — esas lecturas salían del
 *     listener vivo al `getDocs` paginado, que se factura la colección entera
 *     en cada montaje;
 *   · para los caps ≤ 10.000 (Postventa 2.000 y 5.000, Tendencias 300) el
 *     cambio ENCARECE: ahí el `limit` era una cota real sobre un listener
 *     vivo, y ahora se lee todo. Se acepta a propósito: lo que se compra es
 *     que el número que la pantalla muestra sea el verdadero.
 *
 * Lo que NO arregla: esto no elimina el loaded-all. Leer una colección entera
 * en el navegador sigue siendo caro; lo que cambia es que deja de ser
 * **silencioso** y de mentir sobre el total. El loaded-all lo resuelve el
 * rollup server-side, que `PLAN_ACTUAL.md` declara fuera del alcance del ítem
 * 18. El precepto que sostiene este cambio es ADR 066 —"lo que degrada en
 * silencio necesita un check mecánico"— y el check es la alarma, no un
 * `limit()`.
 */
// `superaUmbral` vivía acá. Desde el 1-oct-2026 vive en
// `@core/lib/cotaDeLectura`, junto con la regla: el hook no puede ser la fuente
// de algo que una lectura imperativa también necesita (report du3xh8vn8Gmd8fmmf5aB).

export function useDashboardData({
  collectionName,
  empresaId,
  filters = [],
  orderByField = null,
  orderDir = 'desc',
  limit = null,
  // Umbral de alarma (ver `superaUmbral`). NO recorta. Es independiente de
  // `limit`: se pueden pasar los dos, y cada uno hace lo suyo.
  cap = null,
  // Obligatorio si `cap === SIN_CAP_DECLARADO`. Va acá y no en una allowlist
  // externa para que el motivo se lea al lado de la query que exime.
  motivoSinCap = '',
  enabled = true,
  // 4-oct-2026 (Pulso TN): suscribir con `includeMetadataChanges`. Sin eso,
  // Firestore no avisa cuando la lectura pasa de la caché del navegador a
  // confirmada por el servidor con los mismos datos, y `fromCache` queda en
  // `true` para siempre. Opcional: sólo lo pasa quien espera al servidor para
  // dar un veredicto. No cambia cuánto se lee.
  escucharMetadata = false,
}) {
  // ⭐ EL CANDADO — y su ubicación no es casual, son dos decisiones
  // ─────────────────────────────────────────────────────────────────────────
  // 1. **En el cuerpo del hook, no adentro de `subscribe`.** El cuerpo de
  //    `subscribe` vive dentro de un `try/catch` que convierte cualquier
  //    excepción en estado `error` del dashboard. Puesto ahí, el `throw` de
  //    dev lo tragaría ese catch: la pantalla diría "hubo un error" y el
  //    candado quedaría decorativo, que es justo lo que este ítem persigue.
  // 2. **Antes del `useCallback`**, para que corra aunque `enabled` sea false:
  //    declarar hasta dónde se lee es una propiedad ESTÁTICA del call site, no
  //    algo que dependa de si hoy la pantalla está prendida.
  //
  // ⚠️ Y lo que este brazo NO es: el control. `npm run build` corre sin
  // `--mode`, así que en **staging `import.meta.env.DEV` es `false`** — y
  // staging es donde el equipo mira. Esto acelera el ciclo de quien corre
  // `npm run dev`; el candado de verdad es el checker de CI, que ve el árbol
  // entero. Ver el ADR `2026-08-16-que-cuenta-como-cota-de-lectura...`.
  const cota = resolverCota({
    collectionName, cap, limit, motivoSinCap, orderByField,
    esDev: import.meta.env.DEV,
  })
  // ⚠️ De acá en adelante se usan los valores RESUELTOS, nunca los props. El
  // opt-out se declara como `cap: SIN_CAP_DECLARADO`, que es un **Symbol**, y
  // un Symbol es truthy: comparar `recibidos > Symbol` tira `TypeError`.
  // `resolverCota` ya lo tradujo a `cap: null`.
  const capEfectivo = cota.cap
  const limitDeclarado = cota.limit
  const sinCotaDeclarada = cota.motivoDeAlerta === SIN_DECLARAR_EN_PROD
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState(null)
  const [fromCache, setFromCache] = useState(false)
  const [capExcedido, setCapExcedido] = useState(false)
  const unsubRef = useRef(null)

  const subscribe = useCallback(() => {
    // Cleanup anterior
    if (unsubRef.current) {
      unsubRef.current()
      unsubRef.current = null
    }

    if (!enabled || !collectionName || !empresaId) {
      setData([])
      setCapExcedido(false)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Un filtro con `value` undefined tira acá y cae al catch: error y sin query.
      const baseConstraints = [where('empresaId', '==', empresaId), ...wheresDeFiltros(filters)]
      if (orderByField) {
        baseConstraints.push(orderBy(orderByField, orderDir))
      }

      const limitEfectivo = limitDeclarado ? Math.min(limitDeclarado, FIRESTORE_MAX_LIMIT) : null

      // ⚠️ El grito va UNA sola vez por suscripción. `setRows` corre en CADA
      // snapshot, y un listener vivo sobre una colección grande re-emite ante
      // cada escritura: durante un sync son cientos. Sin este flag, la alarma
      // pasa de señal a inundación — y como el logger flushea de a 20, una
      // inundación de errores tapa los errores de verdad.
      let yaGrito = false
      let yaGritoSinCota = false

      // `cap` NO recorta: cuenta. Ver `superaUmbral` en `@core/lib/cotaDeLectura`.
      const setRows = (docs) => {
        const excedido = superaUmbral({ recibidos: docs.length, cap: capEfectivo })
        // `limit` SÍ recorta —es la cota silenciosa, pedida a propósito— pero
        // que sea deliberada no quita que la vista quede parcial: si se llegó
        // al tope, el caller tiene que poder decirlo.
        const alcanzoLimite = !!limitEfectivo && docs.length >= limitEfectivo
        setCapExcedido(excedido || alcanzoLimite)

        if (excedido && !yaGrito) {
          yaGrito = true
          // ⚠️ TRES argumentos. La firma es `error(mensaje, errObj, ctx)`
          // (`logger.js`): con dos, el contexto cae en la posición de `errObj`,
          // `ctx` queda `undefined` y **`addDoc` rechaza el documento entero**
          // —Firestore no acepta `undefined` y este proyecto no tiene
          // `ignoreUndefinedProperties`—. Y como `addDoc` tira SÍNCRONO, el
          // `.catch` del logger no se engancha y se pierde el lote completo de
          // hasta 20 entradas, incluidos los errores ajenos que viajaban ahí.
          logger.error(MENSAJE_UMBRAL_EXCEDIDO, null, {
            collectionName, recibidos: docs.length, umbral: capEfectivo,
          })
        }

        // La lectura llegó a producción sin declarar hasta dónde lee. NO se
        // recorta —recortar elegiría por nombre de documento, o sea sin
        // criterio— así que el único costo que se paga es el aviso. Se reporta
        // en el primer snapshot y no al montar, a propósito: así el aviso trae
        // CUÁNTO leyó, que es el dato con el que después se le elige un umbral.
        if (sinCotaDeclarada && !yaGritoSinCota) {
          yaGritoSinCota = true
          logger.error(MENSAJE_SIN_COTA, null, {
            collectionName, recibidos: docs.length, motivo: SIN_DECLARAR_EN_PROD,
          })
        }
        // docConId: un campo `id` guardado en el doc no pisa su id — varias de
        // estas colecciones las escribe el cliente y los llamadores escriben
        // por `.id` (reporte ulTf1oZBr4orgUbywUvd).
        setData(docs.map(d => ({ ...docConId(d), _docId: d.id })))
      }

      // ── Un solo camino: listener vivo. `limit` (cota silenciosa, para listas
      // cortas) es lo ÚNICO que acota la query, y se topea en el máximo de
      // Firestore para que un `limit` grande no rompa la lectura entera. ──
      const constraints = [...baseConstraints]
      if (limitEfectivo) constraints.push(fbLimit(limitEfectivo))

      const q = query(collection(db, collectionName), ...constraints)

      const opciones = escucharMetadata ? [{ includeMetadataChanges: true }] : []
      unsubRef.current = onSnapshot(q, ...opciones,
        (snap) => {
          setRows(snap.docs)
          setFromCache(snap.metadata.fromCache)
          setLoading(false)
        },
        (err) => {
          // ⚠️ CON `collectionName`. Este hook alimenta 48+ lecturas: sin la
          // colección adentro del contexto, todos los fallos de listener
          // colapsan en el mismo `scope`+`message` de `clientLogs` y no se
          // puede saber cuál falló. Antes de la migración, cada hook del
          // vertical al menos prefijaba su nombre en el `console.error`.
          logger.error('listener', err, { collectionName })
          setError(err)
          setLoading(false)
        },
      )
    } catch (err) {
      logger.error('subscribe', err, { collectionName })
      setError(err)
      setData([])
      setCapExcedido(false)
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, empresaId, orderByField, orderDir, limitDeclarado, capEfectivo, sinCotaDeclarada, enabled, escucharMetadata, JSON.stringify(filters)])

  useEffect(() => {
    subscribe()
    return () => {
      if (unsubRef.current) {
        unsubRef.current()
        unsubRef.current = null
      }
    }
  }, [subscribe])

  // reload fuerza una re-suscripción (útil después de sync)
  const reload = useCallback(() => {
    subscribe()
  }, [subscribe])

  return { data, loading, error, reload, fromCache, capExcedido }
}
